// CERBERUS through the boss registry. The fight itself lives in sim/boss.ts (unchanged: its brain,
// part volumes and tests predate the registry); this file is the adapter the registry calls.

import { secs } from '../core';
import { ENEMIES, EType } from '../defs';
import type { Reader, Writer } from '../codec';
import { Enemy, historyAt } from '../enemies';
import { BossEv, BossState, Part, PartHit, bossRaycast, bossTookHit, makeBossState, mouthBits, partArmor, stepCerberus } from '../boss';
import { BossDef, BossHit, BossKind, BossNet, Ray } from './types';

/** Cerberus's run-block payload (what the client draws the heads, jaws, core and Ruff from). */
export interface CerberusNet extends BossNet {
  kind: BossKind.Cerberus;
  /** 0..1 per head; 0 with the broken bit set = gone */
  heads: number[];
  broken: number; // bit per head
  mouths: number; // bit per head: jaw hanging open (gold weak point)
  core: boolean;
  crack: number; // 0..1 Ruff crack meter
  howlLos: number; // bit per pid caught in the open by the last howl
}

const NOTE: Record<number, string> = {
  [BossEv.Ruff]: 'STATIC RUFF: HYPER SHIELD UP. PRIME IT, THEN DETONATE.',
  [BossEv.RuffBreak]: 'THE RUFF IS DOWN. IT STAGGERS: OPEN FIRE.',
  [BossEv.Phase3]: 'IT IS FILLING ITS LUNGS. WHEN IT HOWLS, GET BEHIND COVER.',
};

const hp = { x: 0, y: 0, z: 0, yaw: 0 };
const ph: PartHit = { t: 0, part: Part.None, head: -1 };

function body(g: { enemies: Enemy[] }, s: BossState) {
  for (const e of g.enemies) if (e.id === s.eid && !e.dead) return e;
  return null;
}

export const CERBERUS: BossDef<BossState> = {
  kind: BossKind.Cerberus,
  name: 'CERBERUS',
  recall: 'RECALL NOTICE: CERBERUS. JUNKYARD WARDEN, THREE HEADS, NO MANNERS.',
  types: [EType.Cerberus],

  spawn(g, x, z, lookX, lookZ, n, tierMul) {
    const e = g.spawnEnemy(EType.Cerberus, x + 8, z + 6);
    e.maxHp = e.hp = ENEMIES[EType.Cerberus].hp * (1 + 0.35 * (n - 1)) * tierMul;
    e.stDur = secs(2.6);
    // face the posse as it lands
    e.yaw = Math.atan2(-(lookX - e.x), -(lookZ - e.z));
    for (let i = 0; i < e.hyaw.length; i++) e.hyaw[i] = e.yaw;
    return makeBossState(e, n);
  },

  stepEnemy(g, s, e) {
    if (s.eid === e.id) stepCerberus(e, s, g);
  },

  raycast(g, s, r: Ray, view, maxT, out: BossHit) {
    out.t = -1; out.e = null;
    const e = body(g, s);
    if (!e) return out;
    historyAt(e, view, g.tick, hp);
    bossRaycast(hp.x, hp.y, hp.z, hp.yaw, mouthBits(s), s.coreT > 0, r.ox, r.oy, r.oz, r.dx, r.dy, r.dz, maxT, ph);
    if (ph.t < 0) return out;
    const pa = partArmor(ph.part);
    out.t = ph.t; out.e = e; out.av = pa.av; out.mul = pa.mul;
    out.head = ph.part === Part.Head || ph.part === Part.Mouth;
    out.weak = ph.part === Part.Mouth || ph.part === Part.Core;
    out.part = ph.part; out.idx = ph.head;
    return out;
  },

  tookHit(g, s, hit, dealt) {
    if (hit.e) bossTookHit(g, hit.e, s, hit.part as Part, hit.idx, dealt);
  },

  died: (_g, s, e) => e.id === s.eid,

  note(_s, kind, a) {
    if (kind === BossEv.HeadBreak) return `${['FURNACE', 'DYNAMO', 'MAGNET'][a]} HEAD DOWN. THE OTHERS ARE ANGRY.`;
    return NOTE[kind];
  },

  enc(w: Writer, g, b) {
    const e = body(g, b);
    w.u16w(b.eid); w.u8w(b.atk); w.u8w(b.phase | (e && e.hyper ? 8 : 0));
    let broken = 0, mouths = 0;
    for (let i = 0; i < 3; i++) {
      w.u8w(Math.round(Math.max(0, b.headHp[i] / b.headMax) * 255));
      if (b.mouth[i] === -1) broken |= 1 << i;
      if (b.mouth[i] !== 0) mouths |= 1 << i;
    }
    w.u8w(broken | (mouths << 3) | (b.coreT > 0 ? 64 : 0));
    w.u8w(Math.round(Math.min(1, b.crack / 1300) * 255));
    w.u8w(b.howlLos & 255);
  },

  dec(r: Reader): CerberusNet {
    const eid = r.u16(), atk = r.u8(), ph = r.u8();
    const heads = [r.u8() / 255, r.u8() / 255, r.u8() / 255];
    const bits = r.u8();
    const net: CerberusNet = {
      kind: BossKind.Cerberus, eid, atk, phase: ph & 7, heads, broken: bits & 7, mouths: (bits >> 3) & 7, core: (bits & 64) !== 0,
      crack: r.u8() / 255, howlLos: r.u8(),
    };
    if (ph & 8) net.phase |= 8; // hyper (Static Ruff) up
    return net;
  },
};
